import React, { forwardRef, useState, useEffect, useRef } from "react";
import gsap from "gsap";
import { AnnouncementList } from "Shared/Types/fleetmanagementapi";
import styles from "../../styles/Announcements.module.scss";
import { useAnnouncements } from "Components/Announcements/useAnnouncementsModal";
import Button from "Components/UI/Button/Button";
import { cropText, getTimeDiff } from "Shared/Utils/shared";
import cn from "classnames";
import { CalendarIcon } from "Assets";
import Line from "Components/UI/Line/Line";
import { useModal } from "@ebay/nice-modal-react";
import ImageShowModal from "Components/ImageShowModal/ImageShowModal";

type AnnouncementItemProps = {
  announcement: AnnouncementList;
};

const AnnouncementItem = forwardRef<
  HTMLLIElement | null,
  AnnouncementItemProps
>(({ announcement }, ref) => {
  const [isExpanded, setIsExpanded] = useState(
    announcement.description.length > 160,
  );
  const [isLoaded, setIsLoaded] = useState(false);
  const modal = useModal(ImageShowModal);
  const imgRef = useRef(null);
  const itemRef = useRef<HTMLLIElement | null>(null);

  useEffect(() => {
    if (itemRef.current) {
      const tl = gsap.timeline();

      tl.to(itemRef.current, {
        scale: 1,
        opacity: 1,
        duration: 0.6,
        ease: "back.out(1.7)",
        delay: 1,
      });
    }
  }, []);

  const toggleDescription = () => {
    setIsExpanded((prev) => !prev);
  };

  const onShowImage = (src: string) => {
    modal.show({ src });
  };

  const handleImageLoad = () => {
    setIsLoaded(true);
  };

  useEffect(() => {
    if (isLoaded && imgRef.current) {
      gsap.fromTo(imgRef.current, { opacity: 0 }, { opacity: 1, duration: 5 });
    }
  }, [isLoaded]);

  const { getCategoryColor } = useAnnouncements();

  return (
    <li
      className={cn(styles.announcementWrapper__announcement)}
      key={announcement.id}
      ref={(el) => {
        itemRef.current = el;
        if (typeof ref === "function") ref(el);
        else if (ref) ref.current = el;
      }}
    >
      <header className={styles.announcementWrapper__announcement__header}>
        <div
          className={
            styles.announcementWrapper__announcement__header__categoryDate
          }
        >
          <div
            className={cn(
              styles.announcementWrapper__announcement__header__categoryDate__category,
              styles[getCategoryColor(announcement.category)],
            )}
          >
            {announcement.category}
          </div>
          <div
            className={
              styles.announcementWrapper__announcement__header__categoryDate__date
            }
          >
            <h4
              className={
                styles.announcementWrapper__announcement__header__categoryDate__date__text
              }
            >
              {getTimeDiff({
                startDate: announcement.date,
                endDate: new Date(),
              }).text + " ago"}
            </h4>
            <CalendarIcon width={"20px"} />
          </div>
        </div>
        <h4 className={styles.announcementWrapper__announcement__header__title}>
          {announcement.title}
        </h4>
        <Line />
      </header>

      <main className={styles.announcementWrapper__announcement__body}>
        {announcement.photos[0]?.file && (
          <img
            ref={imgRef}
            onClick={() =>
              onShowImage(
                announcement.photos[0]?.file
                  ? announcement.photos[0]?.file
                  : "",
              )
            }
            src={announcement.photos[0]?.file}
            alt="announcement"
            onLoad={handleImageLoad}
          />
        )}
      </main>

      <footer className={styles.announcementWrapper__announcement__footer}>
        <p
          className={
            styles.announcementWrapper__announcement__footer__description
          }
        >
          {!isExpanded
            ? announcement.description
            : cropText({ value: announcement.description, maxLength: 160 })}
        </p>
      </footer>
      {isExpanded && (
        <Button className={styles.button} onClick={toggleDescription}>
          Read More
        </Button>
      )}
    </li>
  );
});

AnnouncementItem.displayName = "AnnouncementItem";

export default AnnouncementItem;
